/** @jsx jsx */
import React, { useEffect, useMemo } from 'react';
import { jsx } from '@emotion/core';
import { Divider } from '@material-ui/core';
import useTranslate, {
  useMarkdownTranslate,
  useMarkdownMultilineTranslate,
} from 'hooks/useTranslate/useTranslate';

import styles from './styles';
import ExpandablePanel from 'components/ExpandablePanel';
import { useSelector } from 'react-redux';
import { selectTenant } from 'state/tenant';

import { conformToMask, decimalMoneyMask } from 'utils/masks';
import useAnalytics, { Event } from 'hooks/useAnalytics';
import BreadTitle from './BreadTitle';

interface CreditCardPaymentOptionProps {
  panelName: string;
  expanded: boolean;
  handlePanelExpandChange: (
    event: React.ChangeEvent<{}>,
    panelName: string,
    expanded: boolean,
  ) => void;
  intendedPurchaseAmount: string | null;
}

const CreditCard: React.FC<CreditCardPaymentOptionProps> = props => {
  const {
    panelName,
    expanded,
    handlePanelExpandChange,
    intendedPurchaseAmount,
  } = props;
  const t = useTranslate();
  const tMd = useMarkdownTranslate();

  const { sendEvents } = useAnalytics();

  const { bread } = useSelector(selectTenant);

  if (!bread) {
    // Should never happens
    throw new Error('Invalid tenant config');
  }

  const totalDiscount = useMemo(() => {
    if (intendedPurchaseAmount && bread.prequalificationDiscount) {
      return bread.prequalificationDiscount * +intendedPurchaseAmount;
    }
    return undefined;
  }, [bread, intendedPurchaseAmount]);

  const percentageDiscount = useMemo(() => {
    if (bread.prequalificationDiscount) {
      return bread.prequalificationDiscount * 100;
    } else return undefined;
  }, [bread]);

  const tMdMultiline = useMarkdownMultilineTranslate();

  useEffect(() => {
    expanded && sendEvents([Event.PrequalInitialOfferExpanded]);
  }, [expanded, sendEvents]);

  const title = (
    <div css={styles.paymentOptionTitleText}>
      <BreadTitle
        id={'creditCardBreadLogo'}
        titlePart={
          <React.Fragment>
            {tMd('unified.offerIntro.paymentOption.creditCard.title')}
            {t('unified.creditCardLogo') !== '' && (
              <img
                src={`data:image/png;base64,${t('unified.creditCardLogo')}`}
                alt={t('unified.creditCardLogoAlt')}
                css={styles.cardIcon}
              />
            )}
          </React.Fragment>
        }
        logo={t('unified.offerIntro.paymentOption.creditCard.breadLogo')}
      />
      <div css={styles.paymentOptionText}>
        {/*As low as <span css={styles.boldColorText}>$50.00/mo*</span> for 12
        months with the Nebella Credit Card for a total payment of $650.20*/}
        {totalDiscount && percentageDiscount
          ? tMd('unified.offerIntro.paymentOption.creditCard.description', {
              percentDiscount: percentageDiscount,
              totalDiscount: conformToMask(
                totalDiscount.toFixed(2),
                decimalMoneyMask,
              ),
            })
          : tMd(
              'unified.offerIntro.paymentOption.creditCard.descriptionWithoutDiscount',
            )}
      </div>
    </div>
  );
  const body = (
    <div css={styles.paymentOptionText} style={{ width: '100%' }}>
      <div css={styles.divider}>
        <Divider />
      </div>
      <div>
        {totalDiscount && percentageDiscount
          ? tMdMultiline('unified.offerIntro.paymentOption.creditCard.perks', {
              percentDiscount: percentageDiscount,
              totalDiscount: conformToMask(
                totalDiscount.toFixed(2),
                decimalMoneyMask,
              ),
            })
          : tMdMultiline(
              'unified.offerIntro.paymentOption.creditCard.perksWithoutDiscount',
            )}
      </div>
    </div>
  );

  return (
    <ExpandablePanel
      title={title}
      children={body}
      panelName={panelName}
      expanded={expanded}
      onChange={handlePanelExpandChange}
    />
  );
};

export default CreditCard;
